import { noop } from 'lodash';
import { ReactElement, useCallback, useMemo } from 'react';
import { useList, useUpdateEffect } from 'react-use';

import { commonTranslationKeys, phoneNumberFormatter } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   FormControllerRenderProps,
   StaticDataConfiguration,
   StyledFlex,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { serviceProvidingLocationTranslationKeys } from '../../../translations';
import { ServiceProvidingLocationRemoveLink } from '../common/ServiceProvidingLocationRemoveLink';
import { ServiceProvidingLocationData } from '../common/ServiceProvidingLocationData';
import { HealthcarePracticeFormBean } from '../common/healthcarePractice';
import { ServiceProvidingLocationFormBean } from '../common/serviceProvidingLocation';

import { ServiceProvidingLocationFromRpwdlUpdateLink } from './ServiceProvidingLocationFromRpwdlUpdateLink';

export const useServiceProvidingLocationsToUpdateDataConfiguration = (
   data: ServiceProvidingLocationFormBean[]
): StaticDataConfiguration<ServiceProvidingLocationFormBean> =>
   useMemo(
      () => ({
         data: data,
      }),
      [data]
   );

// eslint-disable-next-line max-lines-per-function
export const useServiceProvidingLocationsToUpdateColumns = (
   controller: ServiceProvidingLocationsToUpdateController,
   healthcarePractice: HealthcarePracticeFormBean,
   verifyAccountConfiguration: () => Promise<void>
): TableColumnConfiguration<ServiceProvidingLocationFormBean>[] => {
   const { removeServiceProvidingLocation, updateServiceProvidingLocation } = controller;
   const handleServiceProvidingLocationUpdated = useCallback(
      (formData: ServiceProvidingLocationFormBean): void => {
         updateServiceProvidingLocation(formData);
         verifyAccountConfiguration();
      },
      [updateServiceProvidingLocation, verifyAccountConfiguration]
   );
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         locationCode: '160px',
         nameAndAddress: '50%',
         phone: '190px',
         email: '50%',
         options: '60px',
      });

      return [
         {
            label: serviceProvidingLocationTranslationKeys.locationCode,
            width: columnsWidth.locationCode,
            accessor: (bean) => bean.locationCode,
            name: 'locationCode',
            sortingDisabled: true,
         },
         {
            label: serviceProvidingLocationTranslationKeys.nameAndAddress,
            width: columnsWidth.nameAndAddress,

            renderer: (props: ValueRendererProps<ServiceProvidingLocationFormBean, unknown>): JSX.Element => (
               <ServiceProvidingLocationData name={props.bean.serviceProvidingLocationName} address={props.bean} />
            ),
            accessor: noop,
            name: 'name',
            sortingDisabled: true,
         },
         {
            label: commonTranslationKeys.phone,
            width: columnsWidth.phone,
            accessor: (bean) => bean.phoneNumber,
            name: 'phone',
            sortingDisabled: true,
            formatter: phoneNumberFormatter,
         },
         {
            label: commonTranslationKeys.email,
            width: columnsWidth.email,
            accessor: (bean) => bean.emailAddress?.asString(),
            name: 'email',
            sortingDisabled: true,
         },
         {
            name: 'options',
            label: '',
            width: columnsWidth.options,
            accessor: 'locationCode',

            renderer: (props: ValueRendererProps<ServiceProvidingLocationFormBean, unknown>): ReactElement => (
               <StyledFlex>
                  <ServiceProvidingLocationFromRpwdlUpdateLink
                     healthcarePractice={healthcarePractice}
                     serviceProvidingLocation={props.bean}
                     onUpdate={handleServiceProvidingLocationUpdated}
                  />
                  <ServiceProvidingLocationRemoveLink
                     locationCode={props.bean.locationCode}
                     serviceProvidingLocationName={props.bean.serviceProvidingLocationName}
                     // eslint-disable-next-line @typescript-eslint/require-await
                     onRemoved={async () => removeServiceProvidingLocation(props.bean.locationCode)}
                     disabled={props.rowsCount <= 1}
                  />
               </StyledFlex>
            ),
            sortingDisabled: true,
         },
      ];
   }, [healthcarePractice, handleServiceProvidingLocationUpdated, removeServiceProvidingLocation]);
};

export type ServiceProvidingLocationsToUpdateController = {
   serviceProvidingLocations: ServiceProvidingLocationFormBean[];
   updateServiceProvidingLocation: (bean: ServiceProvidingLocationFormBean) => void;
   removeServiceProvidingLocation: (locationCode: string) => void;
};

export const useServiceProvidingLocationsToUpdateController = (
   props: FormControllerRenderProps<ServiceProvidingLocationFormBean[]>
): ServiceProvidingLocationsToUpdateController => {
   const { value, onChange, onBlur } = props;
   const [serviceProvidingLocations, { updateAt, removeAt, set }] = useList<ServiceProvidingLocationFormBean>(
      props.value ?? []
   );

   useUpdateEffect(() => {
      if (value !== serviceProvidingLocations) {
         set(value);
      }
   }, [value]);

   useUpdateEffect(() => {
      if (value !== serviceProvidingLocations) {
         onChange(serviceProvidingLocations);
         onBlur();
      }
   }, [serviceProvidingLocations]);

   const findServiceProvidingLocationIndex = useCallback(
      (locationCode: string): number =>
         serviceProvidingLocations.findIndex(
            (serviceProvidingLocation) => serviceProvidingLocation.locationCode === locationCode
         ),
      [serviceProvidingLocations]
   );

   const updateServiceProvidingLocation = useCallback(
      (bean: ServiceProvidingLocationFormBean): void => {
         const index = findServiceProvidingLocationIndex(bean.locationCode);
         updateAt(index, bean);
      },
      [findServiceProvidingLocationIndex, updateAt]
   );

   const removeServiceProvidingLocation = useCallback(
      (locationCode: string): void => {
         const index = findServiceProvidingLocationIndex(locationCode);
         removeAt(index);
      },
      [findServiceProvidingLocationIndex, removeAt]
   );

   return {
      serviceProvidingLocations: serviceProvidingLocations,
      updateServiceProvidingLocation: updateServiceProvidingLocation,
      removeServiceProvidingLocation: removeServiceProvidingLocation,
   };
};
